export const dashboardRoles = [
  // The built-in roles: the levels the dashboard's authorization is written
  // against. Everything else — including the retired taiyoro-*/playbrain-*
  // legacy names — is a custom role defined on the Roles page and validated by
  // key shape, not by membership in this list.
  "developer",
  "admin",
  "editor",
  "viewer",
] as const

export type DashboardRole = (typeof dashboardRoles)[number]

export type AuthUser = {
  sub?: string
  dashboardLanguage?: "en" | "ja"
  email?: string
  name?: string
  permissions?: unknown
  profileImageUrl?: string
  /** The role key from SQL, hydrated onto the session. Never read from Auth0. */
  role?: unknown
  /** The role keys from SQL, hydrated onto the session. */
  roles?: unknown
  username?: string
  /**
   * Kept only because the users API still mirrors it for consumers that have
   * not migrated. It is never an authorization source here — the roles and
   * permissions come from the database, through the Roles page.
   */
  user_metadata?: {
    role?: unknown
    roles?: unknown
    username?: unknown
  }
  [claim: string]: unknown
}

const dashboardRoleKeyPattern = /^[a-z][a-z0-9-]{1,62}$/

export function isDashboardRole(value: unknown): value is DashboardRole {
  return dashboardRoles.includes(value as DashboardRole)
}

/**
 * The session's roles, as hydrated from SQL by `withDashboardSqlProfile`.
 *
 * Auth0 `user_metadata` is deliberately not consulted: it is a mirror the Roles
 * page cannot reach, so reading it meant a role edited in the dashboard had no
 * effect until the token was reissued.
 */
function rawUserRoles(user: AuthUser | null | undefined): string[] {
  if (!user) return []
  const values = [user.role, user.roles].flatMap((value) => {
    if (Array.isArray(value)) return value
    return value ? [value] : []
  })
  return Array.from(new Set(values.filter((value): value is string => typeof value === "string")))
}

export function isDashboardRoleKey(value: unknown): value is string {
  return (
    typeof value === "string" &&
    dashboardRoleKeyPattern.test(value) &&
    value !== "none" &&
    value !== "superadmin"
  )
}

export function getUserRoleKeys(user: AuthUser | null | undefined): string[] {
  return rawUserRoles(user).filter(isDashboardRoleKey)
}

export function getUserRoles(user: AuthUser | null | undefined): DashboardRole[] {
  const values = rawUserRoles(user)
  return Array.from(new Set(values.filter(isDashboardRole)))
}

export function hasDashboardRole(user: AuthUser | null | undefined, roles: DashboardRole[]): boolean {
  if (roles.length === 0) return Boolean(user)
  const userRoles = getUserRoles(user)
  return roles.some((role) => userRoles.includes(role))
}

function getDashboardPermissions(user: AuthUser | null | undefined): string[] {
  if (!user || !Array.isArray(user.permissions)) return []
  return user.permissions.filter((permission): permission is string => typeof permission === "string")
}

function hasDashboardPermission(user: AuthUser | null | undefined, permission: string): boolean {
  return getDashboardPermissions(user).includes(permission)
}

export function canUseDashboardPermission(
  user: AuthUser | null | undefined,
  resource: string,
  action: string
): boolean {
  return hasDashboardPermission(user, `${resource}:${action}`)
}

export function canEditContent(user: AuthUser | null | undefined): boolean {
  return getDashboardPermissions(user).some((permission) =>
    /^playbrain\.website\.[^:]+:(create|write|publish)$/.test(permission)
  )
}

export function canReadDashboardUsers(user: AuthUser | null | undefined): boolean {
  return hasDashboardPermission(user, "playbrain.users:read")
}

/**
 * Whether the signed-in user may reshape a built-in role.
 *
 * Editing Admin or Viewer changes what every grant naming them conveys, so it
 * is held to role management itself rather than to a role name — the users
 * service enforces the same key. Under the seeded roles that means Developer,
 * Admin and superadmin, which is the intent, expressed in permissions.
 */
export function canEditBuiltInDashboardRoles(user: AuthUser | null | undefined): boolean {
  return hasDashboardPermission(user, "playbrain.roles:write")
}

export function canReadDashboardRoles(user: AuthUser | null | undefined): boolean {
  return hasDashboardPermission(user, "playbrain.roles:read")
}

/**
 * Whether the signed-in user is the Developer built-in (or superadmin).
 *
 * The Roles page and the API documentation portal are Developer-only by
 * product rule — not "whoever holds `playbrain.roles:read`". That permission
 * still gates what the roles service will do, but it travels with grants
 * (Admin holds it, and a `user-management` grant conveys it), which is how
 * Admins and even Viewers ended up seeing both tabs. Identity, not permission,
 * is the product's line here, so this checks the role keys themselves.
 */
export function isDashboardDeveloper(user: AuthUser | null | undefined): boolean {
  const roles = rawUserRoles(user)
  return roles.includes("developer") || roles.includes("superadmin")
}

export function canAdminDashboard(user: AuthUser | null | undefined): boolean {
  return canReadDashboardUsers(user) || canReadDashboardRoles(user)
}
